import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  useWizard,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';

import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCreateSupergraph } from 'src/hooks/useCreateSupergraph';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import { createUniqueName } from 'src/lib/createUniqueName';
import {
  BuildPipelineTrack,
  ConnectorsGraphSetupModalQuery,
  ConnectorsGraphSetupModalQueryVariables,
  CreateServiceKeyMutation,
  CreateServiceKeyMutationVariables,
  GraphType,
  PublishConnectorsSubgraph,
  PublishConnectorsSubgraphVariables,
  UpdateConnectorsBuildPipelineMutation,
  UpdateConnectorsBuildPipelineMutationVariables,
  UserPermission,
} from 'src/lib/graphqlTypes/types';

import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { getCanLoggedInUserCreateHiddenGraphOnAccount } from '../graphSetupModal/canLoggedInUserCreateHiddenGraph';

import { CreateNewSupergraphStepContent } from './CreateNewSupergraphStepContent';
import { LocalDevStepContent } from './LocalDevStepContent';
import {
  DEFAULT_SDLS,
  DEFAULT_VARIANT_NAME,
  DefaultSDLKey,
  FormValues,
  PRODUCTS_DEFAULT_SCHEMA,
} from './shared';

const publishSubgraphMutation = gql<
  PublishConnectorsSubgraph,
  PublishConnectorsSubgraphVariables
>`
  mutation PublishConnectorsSubgraph(
    $graphVariant: String!
    $name: String!
    $revision: String!
    $graphId: ID!
    $sdl: String!
  ) {
    graph(id: $graphId) {
      publishSubgraph(
        graphVariant: $graphVariant
        name: $name
        revision: $revision
        activePartialSchema: { sdl: $sdl }
        url: ""
      ) {
        wasCreated
        subgraphConfigs {
          schemaHash
          sdl
          name
          id
          url
        }
      }
    }
  }
`;

const connectorsGraphSetupModalQuery = gql<
  ConnectorsGraphSetupModalQuery,
  ConnectorsGraphSetupModalQueryVariables
>`
  query ConnectorsGraphSetupModalQuery {
    me {
      id
      ... on User {
        id
        memberships {
          account {
            id
            name
            roles {
              canCreateService
            }
            services {
              id
              title
            }
          }
          user {
            id
          }
        }
      }
    }
  }
`;

const createServiceKeyMutation = gql<
  CreateServiceKeyMutation,
  CreateServiceKeyMutationVariables
>`
  mutation CreateServiceKeyMutation(
    $serviceId: ID!
    $name: String
    $role: UserPermission!
  ) {
    service(id: $serviceId) {
      service {
        id
        variants {
          id
        }
      }
      newKey(keyName: $name, role: $role) {
        id
        token
        keyName
        # This fragment can be removed if/when https://github.com/mdg-private/monorepo/pull/5108 is released
        # and the field can be directly queried
        ... on GraphApiKey {
          role
        }
      }
    }
  }
`;

const updateConnectorsBuildPipelineMutation = gql<
  UpdateConnectorsBuildPipelineMutation,
  UpdateConnectorsBuildPipelineMutationVariables
>`
  mutation UpdateConnectorsBuildPipelineMutation(
    $serviceId: ID!
    $buildPipelineTrack: BuildPipelineTrack!
  ) {
    service(id: $serviceId) {
      defaultBuildPipelineTrack(buildPipelineTrack: $buildPipelineTrack)
    }
  }
`;

export interface ConnectorsOnboardingModalProps {
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
}

const TOTAL_STEP_COUNT = 2;

export const ConnectorsOnboardingModal = ({
  accountId,
  isOpen,
  onClose,
}: ConnectorsOnboardingModalProps) => {
  const client = useApolloClient();

  const [newKey, setNewKey] = useState<string>();
  const [subgraphName, setSubgraphName] = useState('');
  const [graphId, setGraphId] = useState('');

  const trackCustomEvent = useTrackCustomEvent();

  const { activeStep, nextStep, setActiveStep } = useWizard({
    count: TOTAL_STEP_COUNT,
  });

  const { createSupergraph, createSupergraphError, createSupergraphLoading } =
    useCreateSupergraph();
  const currentPlan = useCurrentPlan({
    skip: !accountId,
    accountId,
  });

  useEffect(() => {
    setActiveStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [publishSubgraph, { error: publishError, loading: publishLoading }] =
    useMutation(publishSubgraphMutation, {
      update(cache, { data: publishSubgraphResult }, { variables }) {
        if (!publishSubgraphResult?.graph) return;

        cache.modify({
          id: cache.identify({
            __typename: publishSubgraphResult.graph.__typename,
            id: variables?.graphId,
          }),
          fields: {
            variants: (cachedVariants = [], { toReference }) => {
              const newVariant = toReference({
                __typename: 'GraphVariant',
                id: variables?.graphVariant,
              });
              return [...cachedVariants, newVariant];
            },
          },
        });
      },
    });

  const [
    updateConnectorsBuildPipeline,
    { error: buildPipelineError, loading: buildPipelineLoading },
  ] = useMutation(updateConnectorsBuildPipelineMutation);

  const [addKeyMutation, { error: addKeyError, loading: addKeyLoading }] =
    useMutation<CreateServiceKeyMutation, CreateServiceKeyMutationVariables>(
      createServiceKeyMutation,
      {
        onCompleted: (returnData) => {
          client.cache.evict({
            id: client.cache.identify({
              __typename: returnData.service?.service.__typename,
              id: returnData.service?.service.id,
            }),
            fieldName: 'apiKeys',
          });
          setNewKey(returnData.service?.newKey?.token);
        },
        onError: (e) => {
          throw e;
        },
      },
    );

  const apiErrors = useMemo(() => {
    return [
      createSupergraphError,
      publishError,
      buildPipelineError,
      addKeyError,
    ].filter(Boolean);
  }, [buildPipelineError, createSupergraphError, publishError, addKeyError]);

  const { data, loading } = useQuery(connectorsGraphSetupModalQuery);

  const canLoggedInUserCreateHiddenGraph =
    !!data &&
    getCanLoggedInUserCreateHiddenGraphOnAccount(
      data,
      currentPlan.currentPlan?.userRoles ?? false,
      {
        accountId,
      },
    );

  const memberships = useMemo(
    () => (data?.me?.__typename === 'User' ? data.me.memberships : undefined),
    [data],
  );

  const defaultGraphTitle = useMemo(() => {
    const existingGraphTitles = memberships?.flatMap(({ account }) =>
      account.services.map(({ title }) => title),
    );
    return createUniqueName('My Graph', existingGraphTitles ?? []);
  }, [memberships]);

  const submitCallback = async (formValues: FormValues) => {
    try {
      const result = await createSupergraph({
        accountId: formValues.accountId,
        graphType: GraphType.SELF_HOSTED_SUPERGRAPH,
        hiddenFromUninvitedNonAdmin:
          formValues.hiddenFromUninvitedNonAdminAccountMembers,
        graphId: formValues.graphId,
        title: formValues.graphTitle,
      });

      if (result.data?.account?.createGraph?.__typename === 'Service') {
        // fire ga4 event
        trackCustomEvent({
          action: 'user_action',
          // Old event, this style of category is deprecated
          category: 'Added a graph' as EventCategory,
          label: 'User actions',
          legacyValueDimension: 'deployed',
        });

        await updateConnectorsBuildPipeline({
          variables: {
            serviceId: formValues.graphId,
            buildPipelineTrack: BuildPipelineTrack.FED_NEXT,
          },
        }).catch((err) => {
          if (!err.message.includes('exists')) {
            throw err;
          }
        });

        const sdl =
          formValues.subgraphName === 'products'
            ? PRODUCTS_DEFAULT_SCHEMA
            : (DEFAULT_SDLS[formValues.subgraphName as DefaultSDLKey] ??
              `type Query {
            helloWorld: String
          }`);
        await publishSubgraph({
          variables: {
            graphVariant: DEFAULT_VARIANT_NAME,
            name: formValues.subgraphName,
            revision: '0.0.1',
            graphId: formValues.graphId,
            sdl,
          },
        });
        await addKeyMutation({
          variables: {
            serviceId: formValues.graphId,
            name: DEFAULT_VARIANT_NAME,
            role: UserPermission.GRAPH_ADMIN,
          },
        });

        setGraphId(formValues.graphId);
        setSubgraphName(formValues.subgraphName);

        nextStep();
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const serviceApiKey = newKey || '[Token Unavailable]';
  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      {loading ? (
        <ModalContent>
          <ModalBody className="flex items-center justify-center">
            <Spinner />
          </ModalBody>
        </ModalContent>
      ) : (
        <>
          {addKeyError && <ErrorMessage className="mt-4" error={addKeyError} />}
          {activeStep === 0 ? (
            <CreateNewSupergraphStepContent
              accountId={accountId}
              canLoggedInUserCreateHiddenGraph={
                canLoggedInUserCreateHiddenGraph
              }
              submitCallback={submitCallback}
              graphTitle={defaultGraphTitle}
              onCancel={onClose}
              apiErrors={apiErrors}
              isProcessing={
                addKeyLoading ||
                publishLoading ||
                createSupergraphLoading ||
                buildPipelineLoading
              }
            />
          ) : (
            <LocalDevStepContent
              subgraphName={subgraphName}
              graphRef={`${graphId}@${DEFAULT_VARIANT_NAME}`}
              serviceApiKey={serviceApiKey}
              onClose={onClose}
            />
          )}
        </>
      )}
    </Modal>
  );
};
